import { BuildEnvironment } from "@base/BuildEnvironment";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { IdGen } from "@base/util/IdGen";
import { IArticle } from "@biz/definitions/ArticleDefinitions";
import { FeatureLevel } from "@biz/definitions/ProductDefinitions";
import { ProductVersion } from "@biz/definitions/ProductVersion";
import { CurtainSuspensionType, CurtainTieBackType, CurtainType, DEFAULT_MAX_FIX_DIST, ICurtainCloth, ICurtainClothAuto, ICurtainData, ICurtainTrack } from "@biz/items/curtain/CurtainDefinitions";
import { ClothDataLogic } from "@biz/logic/ClothDataLogic";
import { ShoppingList } from "@biz/models/ShoppingList";
import { IColorItem } from "@biz/SunSailColors";

export class CurtainData {
  static createClothGlobal(curtainType: CurtainType, version: ProductVersion, featureLevel: FeatureLevel, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>): ICurtainClothAuto {
    const defaultSuspensionTypes: Record<CurtainType, CurtainSuspensionType> = {
      "guide": "glider",
      "rope": "carabinerPulley",
      "clothOnly": "none"
    }
    const defaultClothData = ClothDataLogic.getDefaultClothData({ pdt: "curtain", curtainType, version }, featureLevel);

    return {
      cloth: defaultClothData, // defaultClothData,
      colorId: defaultClothData ? ClothDataLogic.getInitialColorId(defaultClothData, articleMap, colorMap, featureLevel) : "",
      height: 2.5,
      width: 2,
      additionFactor: "auto",
      confectionType: "flap",
      confectionBottomType: "lead",
      suspensionType: defaultSuspensionTypes[curtainType],
      tieBackType: "touchFastener",
      maxFixDist: DEFAULT_MAX_FIX_DIST,
    }
  }
  static createEmpty(curtainType: CurtainType, version: ProductVersion, featureLevel: FeatureLevel, _pos: IVec3, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, options?: { name?: string, id?: string }): ICurtainData {
    const curtainData: ICurtainData = {
      itemType: "curtain",
      curtainType,
      version,
      id: options?.id || IdGen.unsecure.get(),
      buildTime: BuildEnvironment.BuildTimeAsNumber,
      clothGlobal: CurtainData.createClothGlobal(curtainType, version, featureLevel, articleMap, colorMap),
      pos: Vec3.from(0, 2.6, 0),
      rotationY: 0,
      count: 1,
      featureLevel,
      shoppingList: ShoppingList.createEmpty(),
      orderType: "H",
      sourceConfigurator: "3d",
      tracks: [
      ]
    }
    return curtainData;
  }

  static create(curtainType: CurtainType, version: ProductVersion, featureLevel: FeatureLevel, pos: IVec3, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, options?: { name?: string, id?: string }): ICurtainData {
    const curtainData = CurtainData.createEmpty(curtainType, version, featureLevel, pos, articleMap, colorMap, options);
    curtainData.tracks.push(CurtainData.createDefaultTrack(pos));

    if (curtainType === "rope") {
      {
        const startCorner = curtainData.tracks[0].corners[0];
        startCorner.articleSlots = {
          ...startCorner.articleSlots,
          startTerminal: "00E13000125",
          startTensioner: "00E13000126",
        }
      }
      {
        const endCorner = curtainData.tracks[0].corners[1];
        endCorner.articleSlots = {
          ...endCorner.articleSlots,
          endTerminal: "00E13000113"
        }
      }
    }
    return curtainData;
  }

  static createDefaultTrack(pos: IVec3): ICurtainTrack {
    const track = CurtainData.createTrack(pos, 2)
    return track;
  }

  static createEmptyTrack(pos: IVec3, initialLength: number): ICurtainTrack {
    return {
      pos,
      angle: 0,
      corners: [
        {
          jointType: "stop",
          length: initialLength,
          angle: 0,
          articleSlots: {}
        },
        {
          jointType: "stop",
          length: initialLength,
          angle: 0,
          articleSlots: {}
        },
      ],
      clothes: []
    }
  }

  static createTrack(pos: IVec3, initialLength: number): ICurtainTrack {
    const track = CurtainData.createEmptyTrack(pos, initialLength);
    track.clothes.push(CurtainData.createCloth());
    return track;
  }

  static createCloth(width: number = 2): ICurtainCloth {
    return {
      shape: "rect",
      // cloth: undefined, // auto
      // colorId: "", // auto
      width,
      tieBackCount: 1,
      tieBackLength: "auto",
      // height: 2.0, // auto
      // additionFactor: 0.1 // auto
    }
  }
}

export function getTieBackArticleNumber(tieBackType: CurtainTieBackType, clothGroup: string): string {
  const system = "54";
  return { grommet: `${system + clothGroup}RH`, touchFastener: `${system + clothGroup}RHKF` }[tieBackType]
}